import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Crosshair, ShieldCheck, Target, Gift, Radar } from "lucide-react";
import { getCategories, getFeatured, getStats } from "@/lib/api";
import { CATEGORY_LIST, CATEGORY_ICONS, CATEGORY_LABELS } from "@/lib/categories";
import DiscountCard from "@/components/DiscountCard";

export default function Home() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [location, setLocation] = useState("");
    const [featured, setFeatured] = useState([]);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        getFeatured().then(setFeatured).catch(() => {});
        getStats().then(setStats).catch(() => {});
        getCategories().catch(() => {});
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (query.trim()) params.set("q", query.trim());
        if (location.trim()) params.set("city", location.trim());
        navigate(`/browse?${params.toString()}`);
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition((pos) => {
            const params = new URLSearchParams();
            params.set("lat", pos.coords.latitude);
            params.set("lng", pos.coords.longitude);
            params.set("radius", "50");
            navigate(`/browse?${params.toString()}`);
        });
    };

    return (
        <div>
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative overflow-hidden border-b border-white/10"
            >
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1579912437766-7896df6d3cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtaWxpdGFyeSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODY3NTcxMTN8MA&ixlib=rb-4.1.0&q=85"
                        alt=""
                        className="w-full h-full object-cover opacity-25 grayscale"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-[#050608] via-[#050608]/85 to-transparent" />
                    <div className="absolute inset-0 bg-tactical-grid opacity-40" />
                    <div className="absolute inset-0 bg-scanlines" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32 grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-8">
                        <div className="mono-label text-volt mb-4">
                            [ CLASSIFIED · TIER-1 DISCOUNT INTEL ]
                        </div>
                        <h1 className="font-oswald font-bold uppercase tracking-wide text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
                            Locate the discounts <span className="text-volt">you earned.</span>
                        </h1>
                        <p className="text-lg text-[#94a3b8] max-w-2xl mb-8 leading-relaxed">
                            SIXCLICK is a tactical directory of verified military discounts and freebies. Sweep any zip, city, or state and see what&apos;s on offer near you — from mess-hall meals to home services.
                        </p>

                        <form
                            onSubmit={submitSearch}
                            data-testid="hero-search-form"
                            className="flex flex-col sm:flex-row gap-2 max-w-2xl mb-4"
                        >
                            <div className="flex items-center gap-2 bg-[#0e1319] border border-white/15 focus-within:border-volt px-3 py-3 flex-1">
                                <Search className="w-4 h-4 text-volt" />
                                <input
                                    data-testid="hero-search-keyword"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search brand, category, or perk"
                                    className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-[#64748b]"
                                />
                            </div>
                            <div className="flex items-center gap-2 bg-[#0e1319] border border-white/15 focus-within:border-volt px-3 py-3 sm:w-64">
                                <MapPin className="w-4 h-4 text-volt" />
                                <input
                                    data-testid="hero-search-location"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="City or ZIP"
                                    className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-[#64748b]"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="hero-search-submit"
                                className="btn-volt justify-center"
                            >
                                <Target className="w-4 h-4" /> Scan
                            </button>
                        </form>

                        <button
                            data-testid="hero-use-location"
                            onClick={useMyLocation}
                            className="mono-label text-volt hover:text-white inline-flex items-center gap-2"
                        >
                            <Crosshair className="w-3.5 h-3.5" /> Use my current position
                        </button>
                    </div>

                    <aside className="md:col-span-4 flex flex-col gap-3">
                        {[
                            { label: "Intel Points", value: stats?.total_discounts ?? "—", icon: Radar },
                            { label: "Freebies Active", value: stats?.freebies ?? "—", icon: Gift },
                            { label: "Cities Covered", value: stats?.cities_covered ?? "—", icon: MapPin },
                            { label: "Community Verified", value: "24/7", icon: ShieldCheck },
                        ].map((s) => (
                            <div
                                key={s.label}
                                className="tactical-card p-4 flex items-center justify-between hud-brackets"
                            >
                                <div>
                                    <div className="mono-label">{s.label}</div>
                                    <div className="font-oswald text-2xl text-volt">{s.value}</div>
                                </div>
                                <s.icon className="w-6 h-6 text-white/40" />
                            </div>
                        ))}
                    </aside>
                </div>
            </section>

            {/* CATEGORIES BENTO */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="mono-label text-volt mb-2">// SECTION 02</div>
                        <h2 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl">
                            Choose Your Sector
                        </h2>
                    </div>
                    <button
                        data-testid="view-all-categories"
                        onClick={() => navigate("/browse")}
                        className="hidden md:inline mono-label text-volt hover:text-white"
                    >
                        View All →
                    </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {CATEGORY_LIST.map((c, i) => {
                        const Icon = CATEGORY_ICONS[c.slug];
                        const span = [0, 5].includes(i) ? "md:col-span-2 md:row-span-2" : "";
                        return (
                            <button
                                key={c.slug}
                                data-testid={`home-category-${c.slug}`}
                                onClick={() => navigate(`/browse?category=${c.slug}`)}
                                className={`tactical-card p-6 flex flex-col justify-between text-left group h-32 md:h-auto ${span}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon className="w-5 h-5 text-volt" />
                                    <span className="mono-label text-[10px]">Sector {String(i + 1).padStart(2, "0")}</span>
                                </div>
                                <div>
                                    <div className="font-oswald text-xl md:text-2xl uppercase tracking-wide leading-tight group-hover:text-volt">
                                        {c.label}
                                    </div>
                                    <div className="mono-label text-[#64748b] mt-2">Recon &rarr;</div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED */}
            {featured.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="mono-label text-volt mb-2">// SECTION 03 · HIGH-VALUE TARGETS</div>
                            <h2 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl">
                                Trending Intel
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featured.slice(0, 6).map((d, i) => (
                            <DiscountCard key={d.id} discount={d} index={i} />
                        ))}
                    </div>
                </section>
            )}

            {/* HOW IT WORKS */}
            <section className="relative border-t border-white/10 py-20 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1461183479101-6c14cd5299c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx0YWN0aWNhbCUyMG1hcHxlbnwwfHx8fDE3ODY3NTcxMTN8MA&ixlib=rb-4.1.0&q=85)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8">
                    <div className="mono-label text-volt mb-2">// PROTOCOL</div>
                    <h2 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl mb-12">
                        Three-step engagement
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { n: "01", t: "Search Your Sector", d: "Filter by city, ZIP, or category. Or drop a pin and scan a 50-mile radius." },
                            { n: "02", t: "Verify the Intel", d: "Every discount lists exactly what you need — from military ID to promo codes." },
                            { n: "03", t: "Redeem & Report", d: "Cash in the perk. If terms have changed, submit an update — your fellow service members are watching." },
                        ].map((s) => (
                            <div key={s.n} className="tactical-card p-6 hud-brackets">
                                <div className="font-oswald text-5xl text-volt mb-3">{s.n}</div>
                                <div className="font-oswald text-lg uppercase tracking-wide mb-2">{s.t}</div>
                                <p className="text-sm text-[#94a3b8] leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-10 flex flex-wrap gap-3">
                        <button
                            data-testid="cta-browse"
                            onClick={() => navigate("/browse")}
                            className="btn-volt"
                        >
                            <Target className="w-4 h-4" /> Begin Recon
                        </button>
                        <button
                            data-testid="cta-submit"
                            onClick={() => navigate("/submit")}
                            className="btn-outline-volt"
                        >
                            + Submit a Discount
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
